import { useCallback, useEffect, useState } from "react";
import { resolveLabName, detectSpecimen, qualifiersAgree } from "../../lambda/shared-utils/lab-name.mjs";

// Public (non-PHI) static data. In dev this goes through the Vite proxy so the
// request is same-origin — the CDN's CORS policy does not allow localhost.
// Override with VITE_STATIC_ASSETS_URL to point at another distribution.
const CLOUD_FRONT_URL =
    import.meta.env.VITE_STATIC_ASSETS_URL ||
    (import.meta.env.DEV ? "/cdn-assets" : "https://d167gjz4odbmln.cloudfront.net");
const FX_RANGE_URL = CLOUD_FRONT_URL + "/fxRange.json";
const TEST_NAME_MAP_URL = CLOUD_FRONT_URL + "/normalize-name-map.json";
const ASSESSMENT_TEMPLATES_URL = CLOUD_FRONT_URL + "/assessment-templates.json";

// Minimum acceptable version of each file, NOT an identity check. A newer file
// is always accepted, so publishing new data never requires a lockstep frontend
// deploy; only a CDN copy that is genuinely older than the code expects is
// refused.
const MIN_DATA_VERSIONS = {
    fxRangeData: 1,
    testNameHashmap: 1,
    assessmentTemplates: 1,
};

let cachedFxRange = { value: null };
let cachedTestNameHashmap = { value: null };
let cachedAssessmentTemplates = { value: null };

const fetchAndCacheData = async (key, url, setData, cacheRef, processJsonData = (data) => data) => {
    // Already loaded on this page — the module-level memo keeps repeated mounts
    // from refetching.
    if (cacheRef.value) {
        setData(cacheRef.value);
        return;
    }

    const minVersion = MIN_DATA_VERSIONS[key];

    // Fetch first, and treat sessionStorage as a fallback rather than the source
    // of truth. Reading the cache first meant that editing one of these files
    // without also bumping its version left every warm client on the old copy
    // indefinitely, with nothing to indicate it — which is exactly how a name-map
    // change reached one device and not another.
    try {
        const response = await fetch(url);
        if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);

        const rawJson = await response.json();
        const fetchedVersion = rawJson?.header?.version;

        if (typeof fetchedVersion === 'number' && fetchedVersion < minVersion) {
            throw new Error(`CDN copy is v${fetchedVersion}, older than the required v${minVersion}`);
        }

        const jsonData = processJsonData(rawJson);
        cacheRef.value = jsonData;
        try {
            sessionStorage.setItem(key, JSON.stringify({ v: fetchedVersion, data: jsonData }));
        } catch { /* quota or private mode — the memo above still serves this page */ }
        setData(jsonData);
        console.log(`Fetched ${key} (v${fetchedVersion})`);
        return;
    } catch (err) {
        console.error(`Fetch error for ${key}:`, err);
    }

    // Offline, or the CDN is unhappy: last known good beats an endless spinner.
    try {
        const stored = JSON.parse(sessionStorage.getItem(key) || 'null');
        if (stored?.data) {
            console.warn(`[bloodeye] serving cached ${key} (v${stored.v}) after a failed fetch`);
            cacheRef.value = stored.data;
            setData(stored.data);
        }
    } catch { /* corrupted cache — nothing more to try */ }
};

/**
 * Printed lab name -> canonical marker name, or the name itself when unknown.
 *
 * Exact map lookup, then lab-name.mjs's guarded cleanup (same rules as the
 * upload Lambda). `entry` is the stored result row: its alias is kept only when
 * the upload Lambda tagged it alias_source "llm" (a guarded LLM resolution), it is
 * a real canonical, and it re-passes the guards here. Older files carry unguarded
 * LLM aliases (urine glucose filed as Glucose), which stay ignored.
 */
export const normalizeWithNameMap = (hashmap, name, entry) => {
    if (typeof name !== "string") return name;
    const { canonical } = resolveLabName(name, hashmap);
    if (canonical) return canonical;
    const alias = entry?.alias;
    if (entry?.alias_source === "llm" && typeof alias === "string"
        && hashmap[alias.toLowerCase()] === alias
        && !detectSpecimen(name) && qualifiersAgree(name, alias)) {
        return alias;
    }
    return name;
};

const useCachedFxRange = () => {
    const [fxRangeData, setFxRangeData] = useState(null);

    useEffect(() => {
        fetchAndCacheData("fxRangeData", FX_RANGE_URL, setFxRangeData, cachedFxRange);
    }, []);

    const getFxRangeWrapper = (testName, sex, menopausal = false) => {
        return getFxRange(fxRangeData, testName, sex, menopausal);
    };

    // Export the wrapper as getFxRange to make data loading easy
    return { fxRangeData, getFxRange: getFxRangeWrapper };
};

export const getFxRange = (fxRangeData, testName, sex, menopausal = false) => {
    if (!fxRangeData || !fxRangeData.body) return null;

    // Check generic first
    if (fxRangeData.body.generic?.[testName]) {
        return fxRangeData.body.generic[testName];
    }

    // TODO restrict sex !== null
    const sexCategory = sex === "female" ? "female" : "male";
    if (fxRangeData.body.special?.[sexCategory]) {
        const specialCategory = fxRangeData.body.special[sexCategory];

        // Check sex-specific generic category
        if (specialCategory.generic?.[testName]) {
            return specialCategory.generic[testName];
        }

        // For females, check menopausal categories
        if (sexCategory === "female") {
            const menopauseCategory = menopausal ? "menopausal" : "premenopausal";
            if (specialCategory[menopauseCategory]?.[testName]) {
                return specialCategory[menopauseCategory][testName];
            }
        }
    }

    return null;
};

const processTestNameMap = (data) => {
    let hashmap = {};

    if (!data || !data.body) {
        console.error("Invalid test name map format");
        return hashmap;
    }

    Object.entries(data.body).forEach(([normalizedName, variants]) => {
        variants.forEach((variant) => {
            hashmap[variant.toLowerCase()] = normalizedName;
        });
    });

    return hashmap;
};

const useCachedTestNameMap = () => {
    const [testNameMap, setTestNameMap] = useState(null);
    useEffect(() => {
        fetchAndCacheData("testNameHashmap", TEST_NAME_MAP_URL, setTestNameMap, cachedTestNameHashmap, processTestNameMap);
    }, []);

    const normalizeTestName = useCallback((name, entry) => (
        normalizeWithNameMap(cachedTestNameHashmap.value || {}, name, entry)
    ), [testNameMap]);

    return { testNameMap, normalizeTestName };
};

const useCachedAssessmentTemplates = () => {
    const [assessmentTemplates, setAssessmentTemplates] = useState(null);
    useEffect(() => {
        fetchAndCacheData(
            "assessmentTemplates",
            ASSESSMENT_TEMPLATES_URL,
            setAssessmentTemplates,
            cachedAssessmentTemplates,
            (data) => data.body || data
        );
    }, []);
    return { assessmentTemplates };
};

export { useCachedFxRange, useCachedTestNameMap, useCachedAssessmentTemplates };